"use client";
import {
  Button,
  Field,
  Input,
  Label,
  makeStyles,
  MessageBar,
  tokens,
} from "@fluentui/react-components";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { chapterImageSize } from "../../constants/ImageConstants";
import {
  useGetCurrentPublishedChapterNames,
  useGetDraftAndPublishedFileNames,
} from "../../hooks/useImages";
import {
  clearErrorsByType,
  handleFileNameChangeUnified,
  handleFileSelection,
  populateFileNameFromFile,
  splitFileName,
  validateImageFormUnified,
} from "../../services/ImageService";
import { ButtonWithLoading } from "../ButtonWithLoading";
import { useGetUserAccessLevel } from "../../hooks/useGetUserAccessLevel";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    maxWidth: "800px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  fileSection: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  actions: {
    display: "flex",
    gap: tokens.spacingHorizontalM,
    justifyContent: "flex-end",
    paddingTop: tokens.spacingVerticalM,
  },
  errorContainer: {
    minHeight: "60px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    maxWidth: "800px",
    margin: "0 auto",
    paddingBottom: "16px",
  },
  errorMessage: {
    wordWrap: "break-word",
    overflowWrap: "break-word",
    wordBreak: "break-word",
    hyphens: "auto",
  },
  filePickerInput: {
    display: "none",
  },
  filePickerButton: {
    width: "fit-content",
  },
  errorList: {
    margin: "10px",
    paddingLeft: "0",
    listStyle: "none",
  },
  fileErrorMessage: {
    marginTop: "0",
  },
  fileErrorMessageWithErrors: {
    marginTop: tokens.spacingVerticalS,
  },
  selectedFileContainer: {
    marginTop: tokens.spacingVerticalS,
  },
  fileNameInputContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "4px",
  },
  fileExtension: {
    color: "#888",
    backgroundColor: "#f3f3f3",
    border: "1px solid #ddd",
    borderRadius: "4px",
    padding: "0 8px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    userSelect: "none",
    pointerEvents: "none",
  },
});

export interface ImageFormData {
  title: string;
  name: string;
  file: File | null;
}

interface ImageFormProps {
  isLoading: boolean;
  onSubmit: (data: ImageFormData) => void;
  onCancel?: () => void;
  imageType?: "chapter" | "activity";
  imageHeightLimit?: number;
  imageWidthLimit?: number;
  acceptTypes?: string;
}

const ImageForm = (props: ImageFormProps) => {
  const {
    isLoading,
    onSubmit,
    onCancel,
    imageType = "activity",
    imageHeightLimit = chapterImageSize.height,
    imageWidthLimit = chapterImageSize.width,
    acceptTypes,
  } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const filePickerRef = useRef<HTMLInputElement>(null);

  const accessLevel = useGetUserAccessLevel();

  // Get published names for duplicate checking
  const { data: publishedChapterNames, isLoading: chapterNamesLoading } =
    useGetCurrentPublishedChapterNames();
  const { data: publishedActivityNames, isLoading: activityNamesLoading } =
    useGetDraftAndPublishedFileNames();

  // Determine which names to use based on image type
  const publishedNames =
    imageType === "chapter" ? publishedChapterNames : publishedActivityNames;
  const isNamesLoading =
    imageType === "chapter" ? chapterNamesLoading : activityNamesLoading;

  const [title, setTitle] = useState("");
  const [name, setName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);

  const handleSubmit = async () => {
    const formData = { title: title.trim(), name: name.trim(), file };

    const validation = await validateImageFormUnified(
      formData,
      imageType,
      publishedNames,
      imageWidthLimit,
      imageHeightLimit,
      t
    );

    if (validation.isValid) {
      onSubmit(formData);
    } else {
      setErrors([...new Set(validation.errors)]);
    }
  };
  const isFormValid = () => {
    return (
      title.trim() !== "" &&
      name.trim() !== "" &&
      file !== null &&
      errors.length === 0 &&
      fileError === null &&
      !isNamesLoading
    );
  };

  const handleFileSelect = async (selectedFile: File) => {
    const result = await handleFileSelection(
      selectedFile,
      imageType,
      imageWidthLimit,
      imageHeightLimit,
      errors,
      t
    );

    setErrors(result.updatedErrors);

    if (result.isValid) {
      setFile(selectedFile);
      setFileError(null);

      // Extract the new extension from the selected file
      const selectedFileExt = selectedFile.name.includes(".")
        ? selectedFile.name.substring(selectedFile.name.lastIndexOf("."))
        : "";

      // If no filename provided, use the file name without extension
      // Otherwise keep the current name but replace with the new extension
      let newFileName: string;
      if (!name.trim()) {
        newFileName =
          populateFileNameFromFile(selectedFile, name) + selectedFileExt;
      } else {
        const currentName = splitFileName(name).name;
        newFileName = currentName + selectedFileExt;
      }

      setName(newFileName);

      // Validate the new filename
      const fileNameResult = handleFileNameChangeUnified(
        newFileName,
        result.updatedErrors,
        publishedNames,
        imageType,
        t
      );
      setErrors(fileNameResult.updatedErrors);
    } else {
      setFile(null);
      setFileError(result.fileError || null);
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.errorContainer}>
        {errors.length > 0 && (
          <MessageBar intent="error">
            <ul className={classes.errorList}>
              {errors.map((error, index) => (
                <li key={index} className={classes.errorMessage}>
                  {error}
                </li>
              ))}
            </ul>
          </MessageBar>
        )}
        {fileError && (
          <MessageBar
            intent="error"
            className={
              errors.length > 0
                ? classes.fileErrorMessageWithErrors
                : classes.fileErrorMessage
            }
          >
            <div className={classes.errorMessage}>{fileError}</div>
          </MessageBar>
        )}
      </div>

      <div className={classes.form}>
        <div className={classes.fileSection}>
          <Field label={t("file")} required>
            <>
              <Button
                appearance="secondary"
                className={classes.filePickerButton}
                disabled={isLoading}
                onClick={() => filePickerRef.current?.click()}
              >
                {t("chooseFile")}
              </Button>
              <input
                ref={filePickerRef}
                type="file"
                accept={acceptTypes}
                onChange={async (e) => {
                  const selectedFile = e.target.files?.[0];
                  if (selectedFile) {
                    await handleFileSelect(selectedFile);
                  } else {
                    // File was cleared - clear file state and errors
                    setFile(null);
                    setFileError(null);
                    // Also clear file-related errors from main errors array
                    setErrors((prev) => clearErrorsByType(prev, "file", t));
                  }
                }}
                disabled={isLoading}
                className={classes.filePickerInput}
              />
            </>
          </Field>
          {file && (
            <div className={classes.selectedFileContainer}>
              <Label>
                {t("selectedFile")}: {file.name}
              </Label>
            </div>
          )}
        </div>

        <div className={classes.field}>
          <Field label={t("title")} required hint={t("imageTitleHint")}>
            <Input
              value={title}
              onChange={(_e, data) => {
                setTitle(data.value);
                // Clear title-related errors when title is provided
                if (data.value.trim()) {
                  setErrors((prev) => clearErrorsByType(prev, "title"));
                }
              }}
              placeholder={t("enterImageTitle")}
              disabled={isLoading}
            />
          </Field>
        </div>

        <div className={classes.field}>
          <Field label={t("fileName")} required hint={t("fileNameHint")}>
            <div className={classes.fileNameInputContainer}>
              <Input
                value={splitFileName(name).name}
                onChange={(_e, data) => {
                  // Combine the edited name with the existing extension
                  const { ext } = splitFileName(name);
                  const newFileName = data.value + ext;
                  setName(newFileName);

                  const result = handleFileNameChangeUnified(
                    newFileName,
                    errors,
                    publishedNames,
                    imageType,
                    t
                  );

                  setErrors(result.updatedErrors);
                }}
                placeholder={t("enterImageFileName")}
                disabled={isLoading}
                style={{ flex: 1 }}
              />
              <span
                className={classes.fileExtension}
                aria-disabled="true"
                tabIndex={-1}
              >
                {splitFileName(name).ext}
              </span>
            </div>
          </Field>
        </div>

        <div className={classes.actions}>
          {accessLevel >= 3 && onCancel && (
            <Button
              appearance="secondary"
              onClick={onCancel}
              disabled={isLoading}
            >
              {t("cancel")}
            </Button>
          )}
          <ButtonWithLoading
            size="large"
            text={t("create")}
            onClick={handleSubmit}
            disabled={isLoading || isNamesLoading || !isFormValid()}
            isLoading={isLoading || isNamesLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default ImageForm;
